// "Why These Targets" — the FI engine's own step-by-step account of how today's targets were built.
//
// Renders meta.target_attribution: the regime base, every overlay in engine order with whether it fired and
// the numbers it compared, then the holdings-level principal-defense stop. Every sentence is written by the
// backend; this card only arranges them. Built after 2026-09-11, when that stop capped VCIT 30% -> 6% and
// ~$2.4M of targets moved while the page headline still read "Calm" with no reason anywhere on screen.
import React from "react";
import { chipClass, CARD_CLASS, MICRO_LABEL, type ChipTone } from "@components/cockpit";
import {
  adoptionSummary,
  capNote,
  engineSteps,
  formatMoves,
  stepStatus,
  type AttributionStep,
  type PrincipalBreach,
  type StepStatus,
  type TargetAttribution,
} from "@lib/fiAttribution";

const ui = {
  cardBg: "var(--card-bg)",
  cardBorder: "var(--card-border)",
  textPrimary: "var(--text-primary)",
  textSecondary: "var(--text-secondary)",
  textMuted: "var(--text-muted)",
  groupBg: "rgba(148, 163, 184, 0.10)",
  shadow: "var(--shadow-soft)",
};
const cardStyle: React.CSSProperties = {
  backgroundColor: ui.cardBg,
  border: `1px solid ${ui.cardBorder}`,
  color: ui.textPrimary,
  boxShadow: ui.shadow,
};
const secondary: React.CSSProperties = { color: ui.textSecondary };
const muted: React.CSSProperties = { color: ui.textMuted };

const STATUS_CHIP: Record<StepStatus, { tone: ChipTone; text: string }> = {
  base: { tone: "info", text: "Starting point" },
  changed: { tone: "warning", text: "Changed targets" },
  not_triggered: { tone: "neutral", text: "Not triggered" },
  off: { tone: "neutral", text: "Off" },
  no_weight_moved: { tone: "neutral", text: "Past a limit · no weight moved" },
};

const signedPct = (v?: number | null, digits = 2) =>
  v == null || Number.isNaN(v) ? "—" : `${v > 0 ? "+" : ""}${(v * 100).toFixed(digits)}%`;
const weightPct = (v?: number | null) => (v == null || Number.isNaN(v) ? "—" : `${(v * 100).toFixed(1)}%`);

const BreachTable: React.FC<{ breaches: PrincipalBreach[] }> = ({ breaches }) => (
  <div className="overflow-x-auto mt-2">
    <table className="text-[11px] min-w-full" data-testid="fi-principal-breaches">
      <thead>
        <tr style={muted}>
          <th className="text-left pr-4 font-normal">Holding</th>
          <th className="text-left pr-4 font-normal">Return vs Cost Basis</th>
          <th className="text-left pr-4 font-normal">Limit</th>
          <th className="text-left pr-4 font-normal">Target</th>
          <th className="text-left font-normal">Cap Lifts If It Recovers</th>
        </tr>
      </thead>
      <tbody>
        {breaches.map((b) =>
          b.lockout ? (
            <tr key={b.ticker} style={{ color: ui.textPrimary }} data-testid={`fi-lockout-${b.ticker}`}>
              <td className="pr-4 font-medium">{b.ticker}</td>
              <td className="pr-4">Sold to zero {b.locked_since ?? "—"}</td>
              <td className="pr-4">Lockout</td>
              <td className="pr-4 whitespace-nowrap">
                {b.capped ? `${weightPct(b.previous_weight)} → ${weightPct(b.cap_weight)}` : `${weightPct(b.cap_weight ?? 0)} (already)`}
              </td>
              <td className="whitespace-nowrap">
                Locked through {b.locked_through ?? "—"}; normal rules from {b.free_from ?? "—"}
              </td>
            </tr>
          ) : (
          <tr key={b.ticker} style={{ color: ui.textPrimary }}>
            <td className="pr-4 font-medium">{b.ticker}</td>
            <td className="pr-4 text-down">{signedPct(b.return_pct)}</td>
            <td className="pr-4">{signedPct(b.limit_pct)}</td>
            <td className="pr-4 whitespace-nowrap">
              {b.capped
                ? `${weightPct(b.previous_weight)} → ${weightPct(b.cap_weight)}`
                : b.cap_weight == null
                ? "no cap configured"
                : `already at or below ${weightPct(b.cap_weight)}`}
            </td>
            <td className="whitespace-nowrap">
              {b.recovery_to_lift_pct == null
                ? "—"
                : `${signedPct(b.recovery_to_lift_pct)}${
                    b.approx_yield_decline_to_lift_bps != null
                      ? ` (≈${Math.round(b.approx_yield_decline_to_lift_bps)}bps lower yields)`
                      : ""
                  }`}
            </td>
          </tr>
          ),
        )}
      </tbody>
    </table>
  </div>
);

const StepRow: React.FC<{ index: number; step: AttributionStep; status: StepStatus }> = ({ index, step, status }) => {
  const chip = STATUS_CHIP[status];
  const moves = status === "changed" ? formatMoves(step.weight_deltas) : "";
  const breaches: PrincipalBreach[] = step.key === "principal_defense" ? step.inputs?.breaches ?? [] : [];
  return (
    <li
      className="rounded px-3 py-2"
      style={{ backgroundColor: ui.groupBg, borderLeft: `3px solid ${status === "changed" ? "#f59e0b" : "transparent"}` }}
      data-step={step.key}
      data-status={status}
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-sm font-medium" style={{ color: ui.textPrimary }}>
          {index}. {step.label}
        </span>
        <span className={chipClass(chip.tone)}>{chip.text}</span>
      </div>
      <p className="text-xs mt-1" style={secondary}>
        {step.why}
      </p>
      {moves && (
        <p className="text-[11px] mt-1 font-mono" style={{ color: ui.textPrimary }}>
          {moves}
        </p>
      )}
      {breaches.length > 0 && <BreachTable breaches={breaches} />}
    </li>
  );
};

export const WhyTheseTargets: React.FC<{
  attribution?: TargetAttribution | null;
  /** Duration of the targets actually returned (portfolio_risk.estimated_duration_years). */
  targetDurationYears?: number | null;
}> = ({ attribution, targetDurationYears }) => {
  const summary = adoptionSummary(attribution);
  if (!attribution || !summary) return null;

  const held = Boolean(attribution.adoption && !attribution.adoption.decomposable);
  const steps = engineSteps(attribution);
  const base = attribution.base;
  const principal = attribution.principal_defense;
  const cap = attribution.duration_cap;
  const note = capNote({
    enforced: cap?.enforced_cap_years,
    regime: cap?.regime_cap_years,
    tightenedBy: cap?.cap_tightened_by,
    unknown: cap?.capital_preservation_unknown,
    regimeKey: cap?.regime_key,
  });
  const baseDuration = attribution.duration?.target_duration_base_years;
  const durationText =
    cap?.enforced_cap_years != null
      ? [
          `Target duration ${targetDurationYears != null ? `${targetDurationYears.toFixed(2)}y` : "—"}`,
          baseDuration != null && !held ? ` (regime base ${baseDuration.toFixed(2)}y)` : "",
          ` against the enforced ${cap.enforced_cap_years.toFixed(2)}y cap`,
          cap.regime_key ? ` for ${cap.regime_key}` : "",
          ".",
          note ? ` ${note}` : "",
        ].join("")
      : null;
  let index = 0;

  return (
    <div className={CARD_CLASS} style={cardStyle} data-testid="fi-why-targets">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-sm font-semibold" style={{ color: ui.textPrimary }}>
          Why These Targets
        </p>
        <span className={chipClass(summary.tone as ChipTone)} data-testid="fi-why-pill">
          {summary.pill}
        </span>
      </div>
      <p className="text-xs mt-1" style={secondary} data-testid="fi-why-summary">
        {summary.text}
      </p>

      {!attribution.error && (base || steps.length > 0) && (
        <>
          <p className={`${MICRO_LABEL} mt-3 mb-1`} style={muted}>
            {held ? "Today's engine suggestion — not adopted" : "How today's targets were built, in engine order"}
          </p>
          <ol className="space-y-2">
            {base && <StepRow index={++index} step={{ ...base, weight_deltas: {} }} status="base" />}
            {steps.map((s) => (
              <StepRow key={s.key} index={++index} step={s} status={stepStatus(s)} />
            ))}
            {principal && !held && <StepRow index={++index} step={principal} status={stepStatus(principal)} />}
          </ol>
        </>
      )}

      {!attribution.error && principal && (held || (!base && steps.length === 0)) && (
        <>
          <p className={`${MICRO_LABEL} mt-3 mb-1`} style={muted}>
            Applied to the held targets
          </p>
          <ol className="space-y-2">
            <StepRow index={1} step={principal} status={stepStatus(principal)} />
          </ol>
        </>
      )}

      {durationText && (
        <p className="text-[11px] mt-3" style={secondary} data-testid="fi-why-duration">
          {durationText}
        </p>
      )}
      <p className="text-[11px] mt-2" style={muted}>
        Every step is re-evaluated on each refresh. Principal defense has no buffer: a modest recovery lifts its cap and the
        targets revert. Advisory only — trades are entered manually in IBKR.
      </p>
    </div>
  );
};

export default WhyTheseTargets;
